import { CometChat } from "@cometchat/chat-sdk-javascript";
import { companyFromDomain } from "./format";

/**
 * Who this user is allowed to DISCOVER — search for, browse, or add to
 * something — as decided server-side from their email domain (see
 * netlify/functions/dbm-directory-scope.js).
 *
 * Scope: discovery only. It deliberately does NOT touch conversations
 * someone is already legitimately part of — existing DMs, and every
 * member of a group they're in, stay fully visible and usable. A team
 * member can still build a group mixing a customer and a supplier, and
 * those people can then talk in it; what they can't do is go looking for
 * each other outside of it.
 *
 * Honest limitation: this filters what the APP shows and fetches. Every
 * user still holds a real CometChat token, and CometChat's own API would
 * answer a hand-crafted request from it regardless of what we render.
 * Enforcing this server-side in our own endpoint wouldn't change that —
 * the ceiling is set by CometChat, not by where we put the check. So
 * treat it as "won't encounter them anywhere in the product", not as a
 * hard multi-tenant wall.
 */

const LOCAL_SESSION_KEY = "dbm-local-session-token";
const SCOPE_URL = process.env.REACT_APP_DIRECTORY_SCOPE_URL || "/.netlify/functions/dbm-directory-scope";

/** Long enough that opening four pickers in a row is one request, short
 * enough that an admin reclassifying a domain takes effect on its own
 * without anyone being told to hard-refresh. */
const TTL_MS = 5 * 60 * 1000;

/** Which company/category a person belongs to — the grouping labels the
 * sidebar's Directory needs. Comes from the server because the domain
 * table itself is admin-only. */
export type PersonOrg = {
  /** From Supabase Auth — the reliable source. CometChat's dbmEmail is
   * only written once that person has signed in, so it's blank for
   * anyone who hasn't. */
  email: string;
  domain: string;
  company: string | null;
  category: string | null;
};

export type DirectoryScope = {
  /** True for internal team members, and for everyone while no domain has
   * been classified yet (rollout state — see the server's computeVisibility). */
  canSeeAll: boolean;
  /** False until an admin classifies the first domain. */
  enforced: boolean;
  category: string | null;
  company: string | null;
  /** Null whenever canSeeAll is true. */
  allowedUids: string[] | null;
  /** uid → org labels, for everyone this viewer may see. */
  people: Record<string, PersonOrg>;
};

/** Fail-open only in the sense of "don't break the app if the scope call
 * itself fails" — a network blip shouldn't empty everyone's search. The
 * alternative (fail closed) turns a transient error into what looks like
 * every colleague vanishing, which is worse for a chat app whose data
 * isn't otherwise protected by this layer anyway. */
const UNRESTRICTED: DirectoryScope = {
  canSeeAll: true,
  enforced: false,
  category: null,
  company: null,
  allowedUids: null,
  people: {},
};

let cached: { at: number; value: Promise<DirectoryScope> } | null = null;

export function fetchDirectoryScope(): Promise<DirectoryScope> {
  if (cached && Date.now() - cached.at < TTL_MS) return cached.value;

  const token = localStorage.getItem(LOCAL_SESSION_KEY);
  if (!token) return Promise.resolve(UNRESTRICTED);

  const value = fetch(SCOPE_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  })
    .then(async (res) => {
      if (!res.ok) throw new Error("scope lookup failed");
      const scope = (await res.json()) as DirectoryScope;
      // Older deploys of the function don't send `people` — default it so
      // every consumer can index into it without a null check.
      const withPeople = { ...scope, people: scope.people || {} };
      lastPeople = withPeople.people;
      return withPeople;
    })
    .catch((e) => {
      console.warn("directory scope lookup failed — leaving the directory unfiltered", e);
      // Don't cache a failure: the very next caller should retry rather
      // than inherit a bad answer for the whole TTL.
      cached = null;
      return UNRESTRICTED;
    });

  cached = { at: Date.now(), value };
  return value;
}

/** Drops the cache so the next lookup re-reads the server — used right
 * after an admin changes a classification, so they can see the effect
 * without waiting out the TTL. */
export function invalidateDirectoryScope(): void {
  cached = null;
  cachedUsers = null;
}

/**
 * Snapshot of the last resolved scope, so a picker can look an email up
 * synchronously inside a filter callback instead of threading async state
 * through every component that has a search box.
 *
 * Safe because every picker awaits fetchVisibleUsers (which awaits the
 * scope) before it has a list to filter — by the time anyone types, this
 * is populated.
 */
let lastPeople: Record<string, PersonOrg> = {};

/**
 * The reliable email for a uid, or "" if unknown.
 *
 * Every people-picker that searches by email needs this: the copy on the
 * CometChat user (`dbmEmail`) is only written once that person has signed
 * in, so filtering on it alone silently matched almost nobody.
 */
export function emailForUid(uid: string | undefined): string {
  return (uid && lastPeople[uid]?.email) || "";
}

/** The company heading for a person: the name an admin set, else a
 * readable form of their domain. One place, so the directory tree, the
 * hover card and search can't drift apart on how a company is spelled. */
export function companyLabelFor(org: PersonOrg | undefined): string | null {
  if (!org) return null;
  return org.company || companyFromDomain(org.domain);
}

/** Filters a fetched user list down to what this viewer may discover.
 * Always excludes the viewer themselves, which every call site wanted
 * anyway and previously repeated by hand. */
export function applyScope(
  users: CometChat.User[],
  scope: DirectoryScope,
  meUid?: string
): CometChat.User[] {
  const withoutMe = users.filter((u) => u.getUid() !== meUid);
  if (scope.canSeeAll || !scope.allowedUids) return withoutMe;
  const allowed = new Set(scope.allowedUids);
  return withoutMe.filter((u) => allowed.has(u.getUid()));
}

/** The common case: fetch the directory and the scope together, and hand
 * back only what this viewer is allowed to see. Every people-picker in
 * the app goes through this rather than calling UsersRequestBuilder
 * directly, so there's one place a rule can be missed instead of five. */
export async function fetchVisibleUsers(meUid?: string, limit = 100): Promise<CometChat.User[]> {
  const [list, scope] = await Promise.all([
    new CometChat.UsersRequestBuilder().setLimit(limit).build().fetchNext(),
    fetchDirectoryScope(),
  ]);
  return applyScope((list as CometChat.User[]) || [], scope, meUid);
}

/** CometChat caps a page at 100, so the browsable directory (unlike the
 * pickers, which only ever need the first page) has to walk them. Capped
 * at MAX_PAGES so a runaway directory can't spin forever. */
const PAGE = 100;
const MAX_PAGES = 10;

async function fetchAllUsers(): Promise<CometChat.User[]> {
  const req = new CometChat.UsersRequestBuilder().setLimit(PAGE).build();
  const out: CometChat.User[] = [];
  for (let i = 0; i < MAX_PAGES; i++) {
    const page = ((await req.fetchNext()) as CometChat.User[]) || [];
    out.push(...page);
    if (page.length < PAGE) break;
  }
  return out;
}

/** Paging the whole directory is up to MAX_PAGES sequential round trips,
 * and flipping between the Chats and Directory zones unmounts the tree —
 * without this, every flip would pay that cost again. */
let cachedUsers: { at: number; value: Promise<CometChat.User[]> } | null = null;

/** Everything the Directory sidebar needs in one go: the people this
 * viewer may see, plus the scope that says how to group them (and whether
 * they should be seeing a directory at all). */
export async function fetchDirectory(
  meUid?: string
): Promise<{ users: CometChat.User[]; scope: DirectoryScope }> {
  if (!cachedUsers || Date.now() - cachedUsers.at >= TTL_MS) {
    const value = fetchAllUsers().catch((e) => {
      cachedUsers = null; // don't let one failure poison the whole TTL
      throw e;
    });
    cachedUsers = { at: Date.now(), value };
  }
  const [list, scope] = await Promise.all([cachedUsers.value, fetchDirectoryScope()]);
  return { users: applyScope(list, scope, meUid), scope };
}
